import {
  baseCompanyEn,
  companyProfile,
  enPreview,
  legacyAboutPage,
  legacyContactPage,
  quickLinksEn
} from './shared'

export const enSite = {
    lang: 'en',
    company: baseCompanyEn,
    nav: [
      { key: 'home', name: 'Home', path: '/en' },
      { key: 'product', name: 'Platform', path: '/en/product' },
      { key: 'about', name: 'About', path: '/en/about' },
      { key: 'contact', name: 'Contact', path: '/en/contact' }
    ],
    labels: {
      language: 'English',
      switchLanguage: '中文',
      businessContact: 'Contact Sales',
      contactUs: 'Contact Us',
      learnCompany: 'About Alekang',
      learnMore: 'Learn More',
      backHome: 'Back Home',
      cooperation: 'Let’s Talk',
      quickLinks: 'Quick Links',
      contactInfo: 'Contact',
      phone: 'QQ',
      email: 'Business',
      address: 'Address',
      qrText: 'Follow us',
      rights: 'All rights reserved',
      phoneAction: 'QQ Chat',
      emailAction: 'Email Us'
    },
    home: {
      hero: {
        eyebrow: 'Clinical Research Collaboration',
        title: 'Move research work through one trusted operating flow',
        description:
          'From protocol modeling and field capture to quality review and analytics delivery, Alekang helps teams reduce handoff gaps, preserve evidence and work faster.',
        notes: [
          'Protocol modeling',
          'Field capture',
          'Quality review',
          'Analytics delivery'
        ],
        stats: [
          { value: 'Flow', label: 'Connected work' },
          { value: 'Roles', label: 'Shared tasks' },
          { value: 'Evidence', label: 'Audit trail' }
        ],
        primaryAction: 'Explore Products',
        secondaryAction: 'Contact Alekang'
      },
      heroFlow: {
        eyebrow: 'Research Operations',
        title: 'Data, tasks and agents in the same workflow',
        status: 'Live collaboration',
        stages: [
          { number: '01', title: 'Protocol', text: 'Study structure and CRF models' },
          { number: '02', title: 'Capture', text: 'Site entry and file upload' },
          { number: '03', title: 'Quality', text: 'Queries, rules and audit' },
          { number: '04', title: 'Analyze', text: 'Metrics, dashboards and reports' },
          { number: '05', title: 'Act', text: 'Reminders and human approval' }
        ],
        taskPanel: {
          title: 'Today\'s research tasks',
          items: [
            { name: 'Visit review', state: 'Open' },
            { name: 'Site query reply', state: 'In progress' },
            { name: 'Weekly metrics', state: 'Scheduled' }
          ]
        },
        agentPanel: {
          title: 'Agent assistance',
          text: 'Detect missing fields, explain abnormal trends and prepare next actions while authorized users keep final approval.',
          signals: ['Missing fields', 'Anomaly notes', 'Insight draft']
        },
        evidence: ['Access isolation', 'Audit trail', 'Human approval']
      },
      platformPreview: enPreview,
      platformHeader: {
        eyebrow: 'Scenarios',
        title: 'Key scenarios for clinical research collaboration',
        description:
          'Cover study modeling, field capture, quality review, analytics and mobile work so teams move projects through one controlled flow.'
      },
      trustPoints: [
        'Unified multi-portal access',
        'Built for clinical workflows',
        'Launch evidence retained'
      ],
      platformModules: [
        {
          number: '01',
          kicker: 'EDC',
          title: 'Capture and quality',
          text: 'Manage studies, sites, subjects, visits, CRFs, queries and audit evidence.',
          items: ['Study modeling', 'CRF entry', 'Query closure']
        },
        {
          number: '02',
          kicker: 'Research',
          title: 'Personal workspace',
          text: 'Bring study context, role tasks, packages, reconciliation and reports into one view.',
          items: ['Study context', 'Role tasks', 'Reconciliation']
        },
        {
          number: '03',
          kicker: 'Data',
          title: 'Analytics and screens',
          text: 'Connect sources, views, charts, dashboards, storyboards and scheduled delivery.',
          items: ['Data sources', 'Chart shelves', 'Storyboards']
        },
        {
          number: '04',
          kicker: 'Mobile',
          title: 'Onsite mobile work',
          text: 'Use WeChat mini programs for field entry, file upload, query handling and signatures.',
          items: ['Field entry', 'File upload', 'Signature']
        }
      ],
      productHeader: {
        eyebrow: 'Product Entry',
        title: 'Enter products through real research workflows',
        description:
          'Start from daily research operations and move into capture, collaboration, analytics and mobile execution.',
        action: 'Explore Core Products'
      },
      products: [
        {
          kicker: '01',
          title: 'Study design and capture',
          path: '/en/product',
          summary: 'Model protocols, sites, subjects, visits and forms in one governed data flow.',
          points: ['Study model', 'Field entry', 'Audit evidence']
        },
        {
          kicker: '02',
          title: 'Role collaboration and quality',
          path: '/en/product',
          summary: 'Help investigators, coordinators, monitors and data managers close tasks together.',
          points: ['Context switch', 'Task queue', 'Query handling']
        },
        {
          kicker: '03',
          title: 'Analytics insight and publishing',
          path: '/en/product',
          summary: 'Turn sources, views and charts into dashboards, storyboards and operation reports.',
          points: ['Metric views', 'Dashboards', 'Scheduled publish']
        },
        {
          kicker: '04',
          title: 'Mobile onsite execution',
          path: '/en/product',
          summary: 'Use mini programs for onsite tasks, file upload, query replies and signatures.',
          points: ['Mini program', 'File capture', 'Signatures']
        }
      ],
      deliveryHeader: {
        eyebrow: 'Delivery',
        title: 'From workflow discovery to production operation',
        description:
          'Migration rehearsals, permission sampling, workflow checks and rollback planning keep launch decisions evidence-based.'
      },
      deliveryFlow: [
        {
          step: '01',
          title: 'Discover',
          text: 'Confirm studies, roles, workflows, data sources and reporting goals.'
        },
        {
          step: '02',
          title: 'Configure',
          text: 'Set up access, forms, workflows, data sources and mobile entries.'
        },
        {
          step: '03',
          title: 'Rehearse',
          text: 'Run migration rehearsals, count checks and workflow validation.'
        },
        {
          step: '04',
          title: 'Operate',
          text: 'Launch, monitor, prepare rollback and improve continuously.'
        }
      ],
      clientsHeader: {
        eyebrow: 'Clients',
        title: 'Long-term work with medical and public health scenarios',
        description:
          'We communicate credibility through real experience rather than exaggerated numbers.'
      },
      clients: [
        {
          name: 'Jiangsu Province Hospital',
          logo: require('@/assets/images/logo/srm.jpeg')
        },
        {
          name: 'Jiangsu Women and Children Health Hospital',
          logo: require('@/assets/images/logo/jsfy.png')
        },
        {
          name: 'Nanjing Drum Tower Hospital',
          logo: require('@/assets/images/logo/njgl.webp')
        },
        {
          name: 'Jiangsu Province Hospital of Chinese Medicine',
          logo: require('@/assets/images/logo/jsszy.webp')
        }
      ],
      aboutPreview: {
        eyebrow: 'About Alekang',
        title: 'Trusted medical data platforms for long-term collaboration',
        description:
          'Alekang focuses on medical and research data platforms with clear models, stable workflows and traceable operations.',
        image: legacyAboutPage.intro.image
      },
      contactCta: {
        eyebrow: 'Contact',
        title: 'Tell us about your scenario and goals',
        description:
          'Whether you need clinical research operations, a researcher workspace, analytics or mobile collaboration, we can help define a practical path.'
      }
    },
    productPage: {
      eyebrow: 'Platform',
      label: 'Clinical Research Data Collaboration',
      title: 'A unified platform for clinical research work',
      intro:
        'Alekang connects study design, field capture, quality review, analytics and mobile collaboration so each role works from the same trusted flow.',
      bannerPreview: {
        theme: 'data',
        eyebrow: 'Command Center',
        title: 'Clinical Research Command Center',
        subtitle:
          'Studies, sites, subjects, quality risks and agents in one operational view',
        status: 'Live collaboration',
        pill: 'Audit ready',
        metrics: [
          { label: 'Active studies', value: '12' },
          { label: 'Open queries', value: '48' },
          { label: 'Entries this week', value: '326' }
        ],
        panels: [
          {
            title: 'Study Operations',
            items: ['Site activation', 'Subject enrollment', 'Visit execution']
          },
          {
            title: 'Quality Signals',
            bars: [
              { label: 'Completeness', value: '91%', ratio: 91 },
              { label: 'Query closure', value: '84%', ratio: 84 },
              { label: 'Site response', value: '88%', ratio: 88 }
            ]
          },
          {
            title: 'Agent Prompts',
            tags: ['Study setup', 'Anomaly notes', 'Insights', 'Task reminders']
          }
        ],
        footer: ['Study context', 'Access boundary', 'Quality evidence']
      },
      bannerCaption:
        'A research-first operating view that brings capture, quality, analytics and agent assistance into one workflow.',
      highlights: [
        'EDC capture',
        'CRF review',
        'Research collaboration',
        'Analytics screens',
        'Agent assistance',
        'Audit evidence'
      ],
      audiences: {
        eyebrow: 'Who it serves',
        title: 'One data view for every research role',
        items: [
          { name: 'Sponsors', text: 'Track study progress, site quality and risk signals.' },
          { name: 'CRO teams', text: 'Coordinate delivery, monitoring, queries and evidence.' },
          { name: 'Sites', text: 'Handle subjects, visits and forms by study context.' },
          { name: 'Investigators', text: 'Review personal tasks, signatures and reports.' },
          { name: 'Data managers', text: 'Close quality rules, queries and data locks.' },
          { name: 'Operations', text: 'Run portfolios with dashboards and scheduled work.' }
        ]
      },
      solutionHeader: {
        eyebrow: 'Solutions',
        title: 'Product capabilities organized by research workflow',
        description:
          'From capture, collaboration and analytics to mobile work and governance, each product maps to a concrete business scenario and role.'
      },
      agentLayer: {
        eyebrow: 'Agent layer',
        title: 'Assist setup, review, analytics and operations without replacing approval',
        items: ['Study setup', 'CRF review', 'Anomaly notes', 'Insight drafts', 'Task reminders'],
        guardrails: ['Human approval', 'Access isolation', 'Audit trail']
      },
      solutions: [
        {
          id: 'capture',
          kicker: 'Trial Capture',
          title: 'EDC capture and study quality control',
          text:
            'For clinical trials, observational studies and multi-site projects, connect design, site activation, subjects, visits, CRFs, queries and audit evidence.',
          tags: ['Study design', 'Enrollment', 'CRF entry', 'Query closure'],
          agent:
            'Agents suggest visit structures, field checks and query drafts, while authorized users keep final approval.',
          visual: {
            type: 'capture',
            eyebrow: 'EDC Lifecycle',
            title: 'From protocol to data lock',
            stages: [
              { step: '01', title: 'Study design', meta: 'Model studies, sites, visits and CRFs' },
              { step: '02', title: 'Site activation', meta: 'Confirm access, parameters and role scope' },
              { step: '03', title: 'Enrollment', meta: 'Screen, enroll, randomize and capture baseline data' },
              { step: '04', title: 'Visit work', meta: 'Enter forms, manage files and resolve queries' },
              { step: '05', title: 'Data lock', meta: 'Review audit records, export and archive evidence' }
            ],
            signals: ['Missing data', 'Logic conflict', 'Outliers', 'Audit trail']
          }
        },
        {
          id: 'workspace',
          kicker: 'Research Collaboration',
          title: 'Personal research workspace',
          text:
            'A focused portal for investigators, coordinators, monitors and data managers, combining study context, task queues, packages, reconciliation and reports.',
          tags: ['Study context', 'Role tasks', 'Packages', 'Reconciliation'],
          agent:
            'Agents prioritize tasks, explain anomalies and draft review notes for human confirmation.',
          visual: {
            type: 'workspace',
            eyebrow: 'Workspace',
            title: 'Role task cockpit',
            contexts: ['Active study', 'Active site', 'User scope', 'Recent objects'],
            columns: [
              { title: 'Today', items: ['Visit review', 'Signature', 'Report download'] },
              { title: 'Data issues', items: ['Query reply', 'Reconciliation', 'Attachment'] },
              { title: 'Analytics', items: ['Package review', 'Result check', 'Export request'] }
            ],
            alerts: ['Overdue item', 'High-risk record', 'Pending setup']
          }
        },
        {
          id: 'analytics',
          kicker: 'Analytics',
          title: 'Analytics and visual dashboards',
          text:
            'For project, quality and operational analytics, provide sources, data views, chart shelves, dashboards, storyboards and scheduled delivery.',
          tags: ['Sources', 'Data views', 'Chart shelves', 'Storyboards'],
          agent:
            'Agents turn natural-language questions into metric drafts, chart suggestions and narrative summaries.',
          visual: {
            type: 'analytics',
            eyebrow: 'Datart / GoView',
            title: 'From data to storyboard',
            sources: ['Source', 'SQL view', 'Chart shelf', 'Dashboard'],
            metrics: ['Enrollment trend', 'Query close rate', 'Site quality'],
            shelfTitle: 'Field shelf',
            shelf: ['Dimension: Site', 'Metric: Enrollment', 'Filter: Phase', 'Sort: Risk level'],
            bars: [
              { label: 'Enroll', value: 86 },
              { label: 'Quality', value: 73 },
              { label: 'Response', value: 91 }
            ],
            table: ['Site ranking', 'Risk list', 'Scheduled publish']
          }
        },
        {
          id: 'mobile',
          kicker: 'Mobile Capture',
          title: 'WeChat mini program for field workflows',
          text:
            'For site and follow-up work, support subject lookup, visits, CRF entry, attachments, queries and signature confirmation.',
          tags: ['Mini program', 'Field entry', 'Attachments', 'Queries'],
          agent:
            'The assistant highlights missing fields, abnormal values and next actions to reduce onsite rework.',
          visual: {
            type: 'mobile',
            eyebrow: 'Mobile EDC',
            title: 'Field workflow',
            phoneTitle: 'Visit task',
            subject: 'Subject S-042 · Visit 3',
            fields: ['Vital signs', 'Medication', 'Adverse event', 'Photo file'],
            action: 'Save and submit',
            assistant: ['2 required fields open', 'Systolic value out of range', 'Query recommended']
          }
        },
        {
          id: 'governance',
          kicker: 'Research Data Governance',
          title: 'Research data assets and governance',
          text:
            'Maintain research objects, field standards, dictionary versions, access boundaries, lineage and quality rules for reuse, analysis and publication.',
          tags: ['Data standards', 'Quality rules', 'Access boundaries', 'Audit evidence'],
          agent:
            'Agents identify field semantics and recommend dictionaries and quality rules, with traceable human review.',
          visual: {
            type: 'catalog',
            eyebrow: 'Data Governance',
            title: 'Assets, lineage and rules',
            lineage: ['Research object', 'Capture field', 'Dictionary', 'Analytic metric'],
            groups: [
              { name: 'Standards', items: ['Fields', 'Dictionaries', 'Units', 'Versions'] },
              { name: 'Quality', items: ['Rules', 'Checks', 'Thresholds', 'Tracking'] },
              { name: 'Access', items: ['Roles', 'Scope', 'Masking', 'Approval'] }
            ],
            policies: ['Tenant isolation', 'Role access', 'Sensitive fields', 'Full audit']
          }
        }
      ],
      summary: {
        eyebrow: 'Value',
        title: 'One platform for multi-role research collaboration',
        text:
          'Each product can run independently or combine into a full clinical research platform while keeping data, access and audit evidence consistent.',
        items: [
          'Unified access',
          'Role collaboration',
          'Quality loop',
          'Agent assistance'
        ]
      },
      cta: {
        title: 'Need a solution for your research scenario?',
        description:
          'Contact us to evaluate the roadmap based on study type, roles and data sources.'
      }
    },
    aboutPage: {
      ...legacyAboutPage,
      hero: {
        eyebrow: 'About',
        title: 'Focused on clinical research and medical data collaboration',
        description:
          'Alekang builds trusted and operable data platform capabilities for medical and research scenarios.',
        image: legacyAboutPage.hero.image
      },
      intro: {
        title: 'Company',
        lead: 'Nanjing Alekang Digital Technology Co., Ltd. focuses on clinical research and medical data collaboration platforms.',
        paragraphs: [
          'We care not only about system launch, but also about whether data remains clear, consistent, trusted and traceable over time.',
          'With unified object models, access boundaries, workflow governance and analytics, we help reduce collaboration cost across roles and organizations.'
        ],
        bullets: [
          'Data capture management: align research objects, field rules and data definitions.',
          'Process traceability: record key actions, workflow milestones and changes.',
          'Collaboration support: configure workflows around organizations, projects and roles.'
        ],
        image: legacyAboutPage.intro.image
      },
      valuesTitle: {
        eyebrow: 'Mission',
        title: 'Make medical data collaboration clearer and more trusted'
      },
      values: [
        {
          title: 'Mission',
          text: 'Reliable capture, trusted governance and secure collaboration',
          icon: legacyAboutPage.values[0].icon
        },
        {
          title: 'Vision',
          text: 'A trusted platform provider for clinical research data collaboration',
          icon: legacyAboutPage.values[1].icon
        },
        {
          title: 'Principle',
          text: 'Clarity, reliability and long-term operation',
          icon: legacyAboutPage.values[2].icon
        },
        {
          title: 'Focus',
          text: 'Build practical data platforms for medical and research teams',
          icon: legacyAboutPage.values[3].icon
        }
      ],
      timelineTitle: {
        eyebrow: 'Timeline',
        title: 'Improving platform capabilities through real scenarios'
      },
      timeline: [
        {
          year: '2026',
          title: 'Intelligent Clinical Research Platform 2.0 launched'
        },
        {
          year: '2025',
          title: 'Chronic Constipation Clinical Research Platform launched'
        },
        { year: '2024', title: 'City-level screening platform launched' },
        {
          year: '2023',
          title: 'Alekang began independent operation',
          highlight: true
        },
        {
          year: '2022',
          title: 'Hospital and out-of-hospital chronic disease collaboration'
        },
        { year: '2021', title: 'Health management platform 2.0 launched' },
        { year: '2018', title: 'Health management platform 1.0 launched' }
      ]
    },
    contactPage: {
      ...legacyContactPage,
      hero: {
        eyebrow: 'Contact',
        title: 'Discuss your platform needs',
        description:
          'Share your scenario, current stage and priorities. We will suggest a practical product and implementation path.'
      },
      infoTitle: {
        eyebrow: 'Contact',
        title: 'Contact channels',
        description: 'Reach us by phone, email or WeChat.'
      },
      cta: {
        eyebrow: 'Before We Talk',
        title: 'Clear context helps us respond faster',
        description:
          'Please include your business direction, current systems, data sources and expected launch timeline when possible.'
      },
      cards: [
        {
          title: 'Phone',
          text: companyProfile.phone,
          href: companyProfile.phoneHref,
          icon: companyProfile.footerIcons.phone
        },
        {
          title: 'Business',
          text: companyProfile.email,
          href: companyProfile.emailHref,
          icon: companyProfile.footerIcons.email
        },
        {
          title: 'Address',
          text: baseCompanyEn.address,
          href: null,
          icon: companyProfile.footerIcons.address
        }
      ],
      qrText: 'Follow us for updates'
    },
    quickLinks: quickLinksEn
  }
